import React, { useState } from 'react';
import {
    Button,
    Box,
    Typography,
    Alert,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Chip
} from '@mui/material';
import { Delete } from '@mui/icons-material';
import BaseDialog from '../common/BaseDialog';
import { MyAppointmentTypeShare, appointmentTypeShareService } from '../../services/appointmentTypeShareService';
import { extractApiErrorMessage } from '../../utils/apiUtils';

interface ManageReceivedSharesDialogProps {
    open: boolean;
    onClose: () => void;
    appointmentTypeName: string;
    color: string;
    shares: MyAppointmentTypeShare[];
    onShareRemoved: (shareId: string) => void;
}

const ManageReceivedSharesDialog: React.FC<ManageReceivedSharesDialogProps> = ({
    open,
    onClose,
    appointmentTypeName,
    color,
    shares,
    onShareRemoved
}) => {
    const [removing, setRemoving] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleRemove = async (share: MyAppointmentTypeShare) => {
        setRemoving(share.id);
        setError(null);

        try {
            await appointmentTypeShareService.removeMyShare(share.id);
            onShareRemoved(share.id);
        } catch (err: any) {
            console.error('Error removing share:', err);
            setError(extractApiErrorMessage(err, 'Failed to remove share'));
        } finally {
            setRemoving(null);
        }
    };

    return (
        <BaseDialog
            open={open}
            onClose={onClose}
            title={
                <Box display="flex" alignItems="center" gap={1}>
                    <Box
                        sx={{
                            width: 16,
                            height: 16,
                            borderRadius: '4px',
                            backgroundColor: color || '#1976d2',
                            flexShrink: 0
                        }}
                    />
                    {appointmentTypeName}
                </Box>
            }
            maxWidth="sm"
            actions={
                <Button onClick={onClose}>Close</Button>
            }
        >
            {error && <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>{error}</Alert>}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                The following users have shared this appointment type with you:
            </Typography>

            {shares.length === 0 ? (
                <Box py={2} textAlign="center">
                    <Typography color="text.secondary">
                        No shares remaining.
                    </Typography>
                </Box>
            ) : (
                <List>
                    {shares.map((share, index) => (
                        <ListItem
                            key={share.id}
                            divider={index < shares.length - 1}
                            sx={{ py: 1.5 }}
                        >
                            <ListItemText
                                primary={share.sharedByUserName || 'Unknown User'}
                                secondary={
                                    <Box component="span">
                                        {share.businessName}
                                        <Chip
                                            label={share.canWrite ? 'Write' : 'Read Only'}
                                            size="small"
                                            color={share.canWrite ? 'primary' : 'default'}
                                            variant="outlined"
                                            sx={{ ml: 1 }}
                                        />
                                    </Box>
                                }
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    aria-label="remove share"
                                    onClick={() => handleRemove(share)}
                                    disabled={removing === share.id}
                                    color="error"
                                    title="Remove this share"
                                >
                                    <Delete />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}
        </BaseDialog>
    );
};

export default ManageReceivedSharesDialog;
